import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import LabelWithInputField from "./LabelWithInputField";
import ListWrapper from "../utils/ListWrapper";
import TextView from "./TextView";
import { BuildContext } from "../classes/BuildContext";

type _TitleWithDateFieldOnChanged = (value: string) => void;

type _TitleWithDateFieldOnTap = () => void;

export interface TitleWithDateFieldProps extends BaseUIProps {
  key?: string;
  name?: string;
  value: string;
  isRequired?: boolean;
  errors?: Array<string>;
  disable?: boolean;
  labelColor?: ui.Color;
  labelFontSize?: number;
  _errorsHash?: number;
  onChanged?: _TitleWithDateFieldOnChanged;
  onTap?: _TitleWithDateFieldOnTap;
}

class _TitleWithDateFieldState extends ObservableComponent<TitleWithDateFieldProps> {
  static defaultProps = {
    name: "",
    value: "",
    isRequired: false,
    disable: false,
    labelColor: null,
    labelFontSize: 14.0,
    errors: [],
    onChanged: null,
    onTap: null,
  };
  dayValue: string = "";
  monthValue: string = "";
  yearValue: string = "";
  monthFocusNode: ui.FocusNode = null;
  dayFocusNode: ui.FocusNode = null;
  yearFocusNode: ui.FocusNode = null;
  isFocused: boolean = false;
  active: boolean = false;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: TitleWithDateFieldProps) {
    super(props);

    this.initState();
  }
  public get name(): string {
    return this.props.name;
  }
  public get value(): string {
    return this.props.value;
  }
  public get isRequired(): boolean {
    return this.props.isRequired;
  }
  public get errors(): Array<string> {
    return this.props.errors;
  }
  public get disable(): boolean {
    return this.props.disable;
  }
  public get labelColor(): ui.Color {
    return this.props.labelColor;
  }
  public get labelFontSize(): number {
    return this.props.labelFontSize;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;

    this.onInit();
  }
  public initListeners(): void {
    this.subscribeToList(this.errors, "errors");

    this.on(
      ["dayFocusNode", "monthFocusNode", "yearFocusNode"],
      this.computeIsFocused
    );

    this.computeIsFocused();

    this.on(
      [
        "dayFocusNode",
        "dayValue",
        "errors",
        "isFocused",
        "isRequired",
        "labelColor",
        "labelFontSize",
        "monthFocusNode",
        "monthValue",
        "name",
        "yearFocusNode",
        "yearValue",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: TitleWithDateFieldProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.name !== this.props.name) {
      this.fire("name", this);
    }

    if (prevProps.value !== this.props.value) {
      this.fire("value", this);
    }

    if (prevProps.isRequired !== this.props.isRequired) {
      this.fire("isRequired", this);
    }

    if (prevProps.errors !== this.props.errors) {
      this.fire("errors", this);
    }

    if (prevProps.disable !== this.props.disable) {
      this.fire("disable", this);
    }

    if (prevProps.labelColor !== this.props.labelColor) {
      this.fire("labelColor", this);
    }

    if (prevProps.labelFontSize !== this.props.labelFontSize) {
      this.fire("labelFontSize", this);
    }
  }
  public setDayValue(val: string): void {
    let isValChanged: boolean = this.dayValue !== val;

    if (!isValChanged) {
      return;
    }

    this.dayValue = val;

    this.onDateChanged();

    this.fire("dayValue", this);
  }
  public setMonthValue(val: string): void {
    let isValChanged: boolean = this.monthValue !== val;

    if (!isValChanged) {
      return;
    }

    this.monthValue = val;

    this.onDateChanged();

    this.fire("monthValue", this);
  }
  public setYearValue(val: string): void {
    let isValChanged: boolean = this.yearValue !== val;

    if (!isValChanged) {
      return;
    }

    this.yearValue = val;

    this.onDateChanged();

    this.fire("yearValue", this);
  }
  public setMonthFocusNode(val: ui.FocusNode): void {
    let isValChanged: boolean = this.monthFocusNode !== val;

    if (!isValChanged) {
      return;
    }

    this.monthFocusNode = val;

    this.fire("monthFocusNode", this);
  }
  public setDayFocusNode(val: ui.FocusNode): void {
    let isValChanged: boolean = this.dayFocusNode !== val;

    if (!isValChanged) {
      return;
    }

    this.dayFocusNode = val;

    this.fire("dayFocusNode", this);
  }
  public setYearFocusNode(val: ui.FocusNode): void {
    let isValChanged: boolean = this.yearFocusNode !== val;

    if (!isValChanged) {
      return;
    }

    this.yearFocusNode = val;

    this.fire("yearFocusNode", this);
  }
  public setIsFocused(val: boolean): void {
    let isValChanged: boolean = this.isFocused !== val;

    if (!isValChanged) {
      return;
    }

    this.isFocused = val;

    this.fire("isFocused", this);
  }
  public computeIsFocused = (): void => {
    try {
      this.setIsFocused(
        this.monthFocusNode.hasFocus ||
          this.dayFocusNode.hasFocus ||
          this.yearFocusNode.hasFocus
      );
    } catch (exception) {
      console.log(" exception in computeIsFocused : " + exception.toString());

      this.setIsFocused(false);
    }
  };
  public setActive(val: boolean): void {
    let isValChanged: boolean = this.active !== val;

    if (!isValChanged) {
      return;
    }

    this.active = val;

    this.fire("active", this);
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      crossAxisAlignment: ui.CrossAxisAlignment.start,
      children: [
        ui.Row({
          crossAxisAlignment: ui.CrossAxisAlignment.start,
          children: [
            this.name !== null && this.name.isNotEmpty
              ? TextView({
                  data: this.name,
                  style: new ui.TextStyle({
                    fontWeight: ui.FontWeight.w500,
                    fontSize: this.labelFontSize,
                    color:
                      this.labelColor !== null
                        ? this.labelColor
                        : ui.HexColor.fromHexInt(0xff000000),
                  }),
                  className: "xf48",
                })
              : [],
            this.isRequired
              ? TextView({
                  data: "*",
                  style: new ui.TextStyle({
                    fontWeight: ui.FontWeight.w500,
                    color: new ui.Color(0xffe30000),
                  }),
                  className: "x186d",
                })
              : [],
          ],
          key: "0",
        }),
        ui.Row({
          crossAxisAlignment: ui.CrossAxisAlignment.start,
          mainAxisAlignment: ui.MainAxisAlignment.start,
          states: ui.joinStates({ "data-c0": this.isFocused }, {}),
          children: [
            ui.Container({
              padding: ui.EdgeInsets.zero,
              width: 56,
              child: LabelWithInputField({
                value: this.monthValue,
                placeHolder: "MM",
                fieldHeight: 35,
                node: this.monthFocusNode,
                activeColor: new ui.Color(0xffffffff),
                inactiveColor: new ui.Color(0xffffffff),
                inputFormatter: new RegExp("^(0[1-9]?|1[0-2]?)"),
                onChanged: (value) => {
                  this.onMonthChangedHandler(value);
                },
              }),
              key: "0",
              className: "x275 hc",
            }),
            TextView({
              data: "/",
              style: new ui.TextStyle({ fontSize: 20 }),
              className: "xc32",
              key: "1",
            }),
            ui.Container({
              padding: ui.EdgeInsets.zero,
              width: 56,
              child: LabelWithInputField({
                value: this.dayValue,
                placeHolder: "DD",
                fieldHeight: 35,
                node: this.dayFocusNode,
                activeColor: new ui.Color(0xffffffff),
                inactiveColor: new ui.Color(0xffffffff),
                inputFormatter: new RegExp(
                  "^(0[1-9]?|1[0-9]?|2[0-9]?|3[0-1]?)$"
                ),
                onChanged: (value) => {
                  this.onDayChangedHandler(value);
                },
              }),
              key: "2",
              className: "xd44 hc",
            }),
            TextView({
              data: "/",
              style: new ui.TextStyle({ fontSize: 20 }),
              className: "xf16",
              key: "3",
            }),
            ui.Container({
              padding: ui.EdgeInsets.zero,
              width: 63,
              child: LabelWithInputField({
                value: this.yearValue,
                placeHolder: "YYYY",
                node: this.yearFocusNode,
                fieldHeight: 35,
                activeColor: new ui.Color(0xffffffff),
                inactiveColor: new ui.Color(0xffffffff),
                inputFormatter: new RegExp("^[0-9]{0,4}$"),
                onChanged: (value) => {
                  this.onYearChangedHandler(value);
                },
              }),
              key: "4",
              className: "xf79 hc",
            }),
          ],
          className: "x05a",
          key: "1",
        }),
        this.errors.isNotEmpty
          ? ui.Column({
              crossAxisAlignment: ui.CrossAxisAlignment.start,
              children: [
                this.errors.expand((item) => [
                  TextView({
                    data: item,
                    style: new ui.TextStyle({
                      fontSize: 14,
                      color: cStyle.tTextViewErrorTextColorOn,
                    }),
                    className: "x51e",
                    key: item?.toString(),
                  }),
                ]),
              ],
            })
          : [],
      ],
      className: ui.join(this.props.className, "TitleWithDateField"),
      ...copyBaseUIProps(this.props),
    });
  }
  public onInit = (): void => {
    this.setMonthFocusNode(new ui.FocusNode());

    this.setDayFocusNode(new ui.FocusNode());

    this.setYearFocusNode(new ui.FocusNode());

    if (this.value !== null && this.value.isNotEmpty) {
      try {
        let values: Array<string> = this.value.split("/");

        this.setDayValue(values[1]);

        this.setMonthValue(values[0]);

        this.setYearValue(values[2]);
      } catch (e) {
        throw new Error(e.toString());
      }
    }
  };
  public onDateChanged = (): void => {
    // check the day month year validation here

    if (
      this.dayValue !== null &&
      this.dayValue.isNotEmpty &&
      this.monthValue !== null &&
      this.monthValue.isNotEmpty &&
      this.yearValue !== null &&
      this.yearValue.isNotEmpty
    ) {
      try {
        let t: string =
          this.monthValue + "/" + this.dayValue + "/" + this.yearValue;

        this.onChanged(t);
      } catch (e) {
        throw new Error(e.toString());
      }
    }
  };
  public onMonthChangedHandler = (value: string): void => {
    this.setMonthValue(value);

    if (value.length === 2) {
      this.monthFocusNode.unfocus();

      this.dayFocusNode.requestFocus();
    }
  };
  public onDayChangedHandler = (value: string): void => {
    this.setDayValue(value);

    if (value.length === 2) {
      this.dayFocusNode.unfocus();

      this.yearFocusNode.requestFocus();
    }
  };
  public onYearChangedHandler = (value: string): void => {
    this.setYearValue(value);

    if (value.length === 4) {
      this.yearFocusNode.unfocus();
    }
  };
  public get onChanged(): _TitleWithDateFieldOnChanged {
    return this.props.onChanged;
  }
  public get onTap(): _TitleWithDateFieldOnTap {
    return this.props.onTap;
  }
}
export default function TitleWithDateField(props: TitleWithDateFieldProps) {
  return React.createElement(
    _TitleWithDateFieldState,
    { ..._TitleWithDateFieldState.defaultProps, ...props },
    ListWrapper.fromInput<string>(props.errors, "errors")
  );
}
